import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

/**
 * PayPal F&F button with required confirmation modal.
 * Reminds the user to select "SENDING TO A FRIEND" before opening paypal.me.
 *
 * Props:
 *  - amount        (number, required)  total to send, auto-fills paypal.me
 *  - children      (node,   optional)  button label override
 *  - className     (string, optional)  custom button classes
 *  - variant       (string, optional)  "primary" (default tiffany), "banner" (small white), "inline"
 *  - testId        (string, optional)  data-testid on the button
 */
export default function PayPalFnfButton({
  amount,
  children,
  className = '',
  variant = 'primary',
  testId = 'paypal-pay-now-btn',
}) {
  const [open, setOpen] = useState(false);
  const formatted = Number(amount || 0).toFixed(2);

  const handleProceed = () => {
    setOpen(false);
    window.open(`https://paypal.me/blkmrktfb/${formatted}`, '_blank', 'noopener,noreferrer');
  };

  const variantClasses = {
    primary:
      'block w-full bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest text-center py-5 transition-colors font-bold text-lg',
    banner:
      'bg-white text-black px-2 sm:px-3 py-1 font-mono text-xs font-bold hover:bg-gray-100 transition-colors',
    inline:
      'bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest text-center py-3 px-4 transition-colors font-bold text-sm',
  };

  const btnClass = className || variantClasses[variant] || variantClasses.primary;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={btnClass}
        data-testid={testId}
      >
        {children ?? `PAY $${formatted} WITH PAYPAL`}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[300] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="paypal-confirm-modal"
        >
          <div
            className="bg-[#0a0a0a] border border-tiffany-500/50 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-zinc-800 px-4 py-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <h2 className="font-mono text-sm text-white tracking-widest">BEFORE YOU PAY</h2>
            </div>
            <div className="p-5 space-y-4">
              <p className="font-mono text-sm text-zinc-300 leading-relaxed">
                On the PayPal screen, make sure to select{' '}
                <span className="text-tiffany-500 font-bold">"SENDING TO A FRIEND"</span>.
              </p>
              <div className="border border-red-500/40 bg-red-500/5 p-3">
                <p className="font-mono text-[11px] text-red-400 leading-relaxed">
                  If you send as <span className="font-bold">"PAYING FOR AN ITEM OR SERVICE"</span>{' '}
                  your payment will be <span className="font-bold">refunded</span> and your order
                  will be <span className="font-bold">canceled</span>.
                </p>
              </div>
              <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 p-3">
                <div>
                  <p className="font-mono text-[10px] text-zinc-500">YOU WILL SEND</p>
                  <p className="font-mono text-xl text-tiffany-500 font-bold">${formatted}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-[10px] text-zinc-500">TO</p>
                  <p className="font-mono text-xs text-white">@BLKMRKTFB</p>
                </div>
              </div>
            </div>
            <div className="border-t border-zinc-800 p-3 grid grid-cols-2 gap-2">
              <Button
                onClick={() => setOpen(false)}
                variant="outline"
                className="rounded-none border-zinc-800 text-zinc-300 hover:bg-zinc-900 font-mono text-xs"
                data-testid="paypal-confirm-cancel"
              >
                CANCEL
              </Button>
              <Button
                onClick={handleProceed}
                className="rounded-none bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest font-bold"
                data-testid="paypal-confirm-proceed"
              >
                CONTINUE
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
